import { useMutation, useQuery } from "@tanstack/react-query";
import { scheduleContract } from "@common/contracts/schedule.contract";
import {
    ConfirmAllRecognitionPeriodsBody,
    ConfirmRecognitionPeriodsBody,
    ConfirmRecognitionPeriodsResponse,
    ConfirmScheduleDraftsBody,
    ConfirmScheduleDraftsResponse,
    ListRecognitionReviewPeriodsResponse,
    ScheduleDraftApplicationIdsBody,
    ScheduleSuspiciousTransactionIdsBody,
} from "@common/schemas/domains/accounting/scheduleReview.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useScope } from "@state/scopeContext.lib";
import { invalidateTransactionsQueries } from "@components/transactions/useTransactionsQuery";
import { extractApiErrorMessage } from "./apiError";
import { organizationTaskQueryKeys } from "./organizationTask.api";
import { invalidateScheduleTemplates } from "./scheduleTemplate.api";
import { tasksQueryKeys } from "./taskQueryKeys";
import { queryClient } from "../queryClient";

const scheduleReviewClient = getBackendAPIClient(scheduleContract);

export const scheduleReviewQueryKeys = {
    all: (workspaceId: string) => ["schedule-review", workspaceId] as const,
    recognition: (workspaceId: string, entityId: number, periodKey: string) =>
        [
            ...scheduleReviewQueryKeys.all(workspaceId),
            "recognition",
            entityId,
            periodKey,
        ] as const,
};

/**
 * Refreshes everything a schedule review action can change: the review
 * queries, transaction lists, templates, and the task reads. The review task
 * itself closes or reopens later, in the backend resync job, and that change
 * reaches the UI through the task-saved live event; the task invalidation
 * here only covers what has already changed by the time the action returns.
 */
export function invalidateScheduleReview(workspaceId: string): void {
    void queryClient.invalidateQueries({
        queryKey: scheduleReviewQueryKeys.all(workspaceId),
    });
    void invalidateTransactionsQueries();
    invalidateScheduleTemplates(workspaceId);
    void queryClient.invalidateQueries({ queryKey: tasksQueryKeys.all() });
    void queryClient.invalidateQueries({
        queryKey: organizationTaskQueryKeys.all(),
    });
}

function useRequiredWorkspaceId(): string {
    const { workspaceId } = useScope();
    return workspaceId ?? "";
}

export function useRecognitionReviewPeriods(options: {
    entityId: number | null;
    periodKey: string | null;
}) {
    const workspaceId = useRequiredWorkspaceId();
    const { entityId, periodKey } = options;
    return useQuery({
        queryKey: scheduleReviewQueryKeys.recognition(
            workspaceId,
            entityId ?? 0,
            periodKey ?? "",
        ),
        enabled: workspaceId !== "" && entityId != null && periodKey != null,
        queryFn: async (): Promise<ListRecognitionReviewPeriodsResponse> => {
            const response =
                await scheduleReviewClient.listRecognitionReviewPeriods({
                    params: { workspaceId },
                    query: {
                        entityId: entityId ?? 0,
                        periodKey: periodKey ?? "",
                    },
                });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to load recognition periods",
                    ),
                );
            }
            return response.body;
        },
    });
}

export function useConfirmRecognitionPeriodsMutation() {
    const workspaceId = useRequiredWorkspaceId();
    return useMutation({
        mutationFn: async (
            body: ConfirmRecognitionPeriodsBody,
        ): Promise<ConfirmRecognitionPeriodsResponse> => {
            const response =
                await scheduleReviewClient.confirmRecognitionPeriods({
                    params: { workspaceId },
                    body,
                });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to confirm recognition",
                    ),
                );
            }
            return response.body;
        },
        onSuccess: () => invalidateScheduleReview(workspaceId),
    });
}

export function useConfirmAllRecognitionPeriodsMutation() {
    const workspaceId = useRequiredWorkspaceId();
    return useMutation({
        mutationFn: async (
            body: ConfirmAllRecognitionPeriodsBody,
        ): Promise<ConfirmRecognitionPeriodsResponse> => {
            const response =
                await scheduleReviewClient.confirmAllRecognitionPeriods({
                    params: { workspaceId },
                    body,
                });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to confirm recognition",
                    ),
                );
            }
            return response.body;
        },
        onSuccess: () => invalidateScheduleReview(workspaceId),
    });
}

function useSuspiciousMutation(
    action: "dismissSuspiciousTransactions" | "restoreSuspiciousTransactions",
    fallbackMessage: string,
) {
    const workspaceId = useRequiredWorkspaceId();
    return useMutation({
        mutationFn: async (
            body: ScheduleSuspiciousTransactionIdsBody,
        ): Promise<number[]> => {
            const response = await scheduleReviewClient[action]({
                params: { workspaceId },
                body,
            });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(response.body, fallbackMessage),
                );
            }
            return response.body.transactionIds;
        },
        onSuccess: () => invalidateScheduleReview(workspaceId),
    });
}

export function useDismissSuspiciousTransactionsMutation() {
    return useSuspiciousMutation(
        "dismissSuspiciousTransactions",
        "Failed to dismiss transactions",
    );
}

export function useRestoreSuspiciousTransactionsMutation() {
    return useSuspiciousMutation(
        "restoreSuspiciousTransactions",
        "Failed to restore transactions",
    );
}

export function useConfirmScheduleDraftsMutation() {
    const workspaceId = useRequiredWorkspaceId();
    return useMutation({
        mutationFn: async (
            body: ConfirmScheduleDraftsBody,
        ): Promise<ConfirmScheduleDraftsResponse> => {
            const response = await scheduleReviewClient.confirmScheduleDrafts({
                params: { workspaceId },
                body,
            });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to confirm schedules",
                    ),
                );
            }
            return response.body;
        },
        onSuccess: () => invalidateScheduleReview(workspaceId),
    });
}

function useDraftIdsMutation(
    action: "dismissScheduleDrafts" | "restoreScheduleDrafts",
    fallbackMessage: string,
) {
    const workspaceId = useRequiredWorkspaceId();
    return useMutation({
        mutationFn: async (
            body: ScheduleDraftApplicationIdsBody,
        ): Promise<string[]> => {
            const response = await scheduleReviewClient[action]({
                params: { workspaceId },
                body,
            });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(response.body, fallbackMessage),
                );
            }
            return response.body.applicationIds;
        },
        onSuccess: () => invalidateScheduleReview(workspaceId),
    });
}

export function useDismissScheduleDraftsMutation() {
    return useDraftIdsMutation(
        "dismissScheduleDrafts",
        "Failed to dismiss schedule suggestions",
    );
}

export function useRestoreScheduleDraftsMutation() {
    return useDraftIdsMutation(
        "restoreScheduleDrafts",
        "Failed to restore schedule suggestions",
    );
}
